import { useEffect, useState } from "react";
import { X, Plus, Trash, Calendar, Bus, UserCircle, IdentificationCard, MapPin } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useDepots, fmtDepot } from "@/lib/depots";
import { toast } from "sonner";

const DOW = [
  { key: 0, label: "Mon" }, { key: 1, label: "Tue" }, { key: 2, label: "Wed" },
  { key: 3, label: "Thu" }, { key: 4, label: "Fri" }, { key: 5, label: "Sat" }, { key: 6, label: "Sun" },
];

const RECURRENCES = [
  { value: "daily",    label: "Daily",    hint: "Every day in range" },
  { value: "weekly",   label: "Weekly",   hint: "Specific weekdays" },
  { value: "monthly",  label: "Monthly",  hint: "Same day each month" },
  { value: "yearly",   label: "Yearly",   hint: "Same date each year" },
  { value: "custom",   label: "Custom",   hint: "Only the start & end dates" },
];

const mLbl = (m) => `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`;
const tmm = (str) => { const [h, m] = (str || "06:00").split(":").map(Number); return h*60 + m; };

/* ========================== MASTER SCHEDULE BUILDER ========================== */
export function MasterScheduleModal({ open, onClose, onCreated, routes, buses, drivers, conductors }) {
  const depotMap = useDepots();
  const today = new Date().toISOString().slice(0,10);
  const inAMonth = new Date(Date.now() + 30*86400000).toISOString().slice(0,10);
  const [form, setForm] = useState({
    name: "", bus_id: "", driver_id: "", conductor_id: "",
    start_date: today, end_date: inAMonth,
    recurrence: "daily", days_of_week: [0,1,2,3,4],
    routes: [],
  });
  const [routeRows, setRouteRows] = useState([
    { route_id: "", start_time: "06:00", duration_min: 60, sequence: 1 },
  ]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) {
    setForm(f => ({ ...f, name: "" }));
    setRouteRows([{ route_id: "", start_time: "06:00", duration_min: 60, sequence: 1 }]);
  } }, [open]);

  if (!open) return null;

  const addRoute = () => setRouteRows([...routeRows, { route_id: "", start_time: "06:00", duration_min: 60, sequence: routeRows.length + 1 }]);
  const updateRoute = (i, k, v) => setRouteRows(routeRows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeRoute = (i) => setRouteRows(routeRows.filter((_, idx) => idx !== i));
  const toggleDOW = (k) => setForm({ ...form, days_of_week: form.days_of_week.includes(k) ? form.days_of_week.filter(d => d !== k) : [...form.days_of_week, k] });

  const submit = async () => {
    if (!form.name) { toast.error("Give the schedule a name"); return; }
    if (!form.bus_id) { toast.error("Pick a bus"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Date range is required"); return; }
    if (form.recurrence === "weekly" && form.days_of_week.length === 0) { toast.error("Pick at least one weekday"); return; }
    const validRows = routeRows.filter(r => r.route_id);
    if (validRows.length === 0) { toast.error("Add at least one route"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        routes: validRows.map(r => ({
          route_id: r.route_id, start_min: tmm(r.start_time),
          duration_min: +r.duration_min, sequence: +r.sequence,
        })),
      };
      const { data } = await api.post("/schedules", payload);
      toast.success(`Schedule "${data.name}" created · ${data.expanded_trips} trips across ${data.dates_count} dates`);
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create schedule");
    } finally { setBusy(false); }
  };

  // Date-range preview
  const estimatedDates = () => {
    const s = new Date(form.start_date); const e = new Date(form.end_date);
    if (e < s) return 0;
    const totalDays = Math.floor((e - s) / 86400000) + 1;
    if (form.recurrence === "daily") return totalDays;
    if (form.recurrence === "weekly") {
      let count = 0; const cur = new Date(s);
      while (cur <= e) { if (form.days_of_week.includes((cur.getDay() + 6) % 7)) count++; cur.setDate(cur.getDate() + 1); }
      return count;
    }
    if (form.recurrence === "monthly") return Math.max(1, Math.floor(totalDays / 30) + 1);
    if (form.recurrence === "yearly") return Math.max(1, Math.floor(totalDays / 365) + 1);
    if (form.recurrence === "custom") return form.start_date === form.end_date ? 1 : 2;
    return 0;
  };
  const tripsPreview = estimatedDates() * routeRows.filter(r => r.route_id).length;

  return (
    <div className="fixed inset-0 bg-black/50 z-[1300] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div data-testid="master-schedule-modal" className="bg-white w-full max-w-3xl max-h-[90dvh] sm:max-h-[92vh] overflow-auto rounded-t-2xl sm:rounded-lg" onClick={e=>e.stopPropagation()}>
        <div className="sticky top-0 bg-white p-4 sm:p-5 border-b border-slate-200 flex items-start justify-between z-10 rounded-t-2xl sm:rounded-t-lg">
          <div>
            <div className="flex items-center gap-2"><Calendar size={20} className="text-[#1E3A8A]"/><h3 className="font-display text-xl font-bold">Master Schedule Builder</h3></div>
            <div className="text-xs text-slate-500 mt-0.5">1 bus + 1 crew · multiple routes · across day/week/month/year</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Name */}
          <Section title="Schedule Name *">
            <input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder='e.g. "KT-1127 · Panaji ↔ Margao · Weekday Loop"'
                   className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
          </Section>

          {/* Bus + Crew */}
          <Section title="Bus & Crew Assignment *">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Sel icon={Bus} label="Bus *" value={form.bus_id} on={v=>setForm({...form, bus_id:v})} options={buses.map(b=>({value:b.fleet_id, label:`${b.fleet_id} · ${fmtDepot(b.depot, depotMap)}`}))} placeholder="Select bus"/>
              <Sel icon={UserCircle} label="Driver" value={form.driver_id} on={v=>setForm({...form, driver_id:v})} options={drivers.map(d=>({value:d.name, label:`${d.name} · ${fmtDepot(d.depot, depotMap)}`}))} placeholder="Optional"/>
              <Sel icon={IdentificationCard} label="Conductor" value={form.conductor_id} on={v=>setForm({...form, conductor_id:v})} options={conductors.map(c=>({value:c.name, label:`${c.name} · ${fmtDepot(c.depot, depotMap)}`}))} placeholder="Optional"/>
            </div>
          </Section>

          {/* Date range */}
          <Section title="Date Range *">
            <div className="grid grid-cols-2 gap-2">
              <Lbl label="Start Date"><input data-testid="schedule-start-date" type="date" value={form.start_date} onChange={e=>setForm({...form, start_date:e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/></Lbl>
              <Lbl label="End Date"><input data-testid="schedule-end-date" type="date" value={form.end_date} onChange={e=>setForm({...form, end_date:e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/></Lbl>
            </div>
          </Section>

          {/* Recurrence */}
          <Section title="Recurrence Pattern *">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {RECURRENCES.map(r => (
                <button key={r.value} data-testid={`recurrence-${r.value}`} onClick={()=>setForm({...form, recurrence:r.value})}
                        className={`px-2 py-2 border rounded-md text-sm text-left ${form.recurrence===r.value?"border-[#1E3A8A] bg-blue-50 text-[#1E3A8A] font-semibold":"border-slate-200 hover:bg-slate-50"}`}>
                  <div>{r.label}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{r.hint}</div>
                </button>
              ))}
            </div>
            {form.recurrence === "weekly" && (
              <div className="mt-3">
                <div className="text-[11px] text-slate-500 mb-2 font-semibold uppercase tracking-wide">Active Days</div>
                <div className="flex flex-wrap gap-1.5">
                  {DOW.map(d => (
                    <button key={d.key} onClick={()=>toggleDOW(d.key)}
                            className={`w-12 h-9 border rounded-md text-xs font-semibold ${form.days_of_week.includes(d.key)?"border-[#1E3A8A] bg-blue-50 text-[#1E3A8A]":"border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {/* Routes (the per-day trip plan) */}
          <Section title={`Routes for this bus · ${routeRows.filter(r=>r.route_id).length} routes per day`}>
            <div className="space-y-2">
              {routeRows.map((row, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 sm:gap-2 items-center bg-slate-50/50 border border-slate-200 rounded-md p-2">
                  <div className="col-span-1 text-xs text-center font-bold text-slate-500">#{row.sequence}</div>
                  <div className="col-span-12 sm:col-span-5">
                    <select value={row.route_id} onChange={e=>updateRoute(i, "route_id", e.target.value)} className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded text-xs outline-none">
                      <option value="">— Select Route —</option>
                      {routes.map((r, idx) => <option key={`${r.route_id}__${idx}`} value={r.route_id}>{`${r.route_id} · ${r.name}`}</option>)}
                    </select>
                  </div>
                  <div className="col-span-5 sm:col-span-2"><input type="time" value={row.start_time} onChange={e=>updateRoute(i, "start_time", e.target.value)} className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded text-xs outline-none"/></div>
                  <div className="col-span-5 sm:col-span-3"><input type="number" value={row.duration_min} onChange={e=>updateRoute(i, "duration_min", e.target.value)} placeholder="min" className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded text-xs outline-none"/></div>
                  <button onClick={()=>removeRoute(i)} className="col-span-1 text-slate-400 hover:text-red-600"><Trash size={14}/></button>
                </div>
              ))}
              <button onClick={addRoute} className="w-full py-2 border-2 border-dashed border-slate-300 rounded-md text-sm text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-1"><Plus size={14}/>Add another route</button>
            </div>
          </Section>

          {/* Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-center justify-between text-sm">
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide">Will generate</div>
              <div className="font-display font-bold text-lg text-[#1E3A8A]">{tripsPreview} trips across {estimatedDates()} dates</div>
            </div>
            <Calendar size={32} weight="duotone" className="text-[#1E3A8A]"/>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-md text-sm">Cancel</button>
          <button data-testid="schedule-create-submit" disabled={busy} onClick={submit} className="ktcl-btn-primary px-5 py-2 rounded-md text-sm font-semibold disabled:opacity-60">
            {busy ? "Creating…" : `Create Schedule · ${tripsPreview} trips`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========================== QUICK ASSIGN TRIP MODAL ========================== */
export function QuickAssignModal({ open, day, slotMin, onClose, onCreated, routes, buses, drivers, conductors }) {
  const depotMap = useDepots();
  const [form, setForm] = useState({ route_id: "", bus_id: "", driver_id: "", conductor_id: "", duration_min: 60, start_min: slotMin || 360 });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm(f => ({ ...f, start_min: slotMin ?? f.start_min, route_id: "", bus_id: "", driver_id: "", conductor_id: "" })); }, [open, slotMin]);

  if (!open) return null;

  const submit = async () => {
    if (!form.route_id) { toast.error("Pick a route"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/trips", { ...form, day });
      toast.success(`Trip ${data.route_id} · ${day} ${mLbl(data.start_min)}`);
      onCreated?.(data);
      onClose();
    } catch { toast.error("Failed to create trip"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[1300] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div data-testid="quick-assign-modal" className="bg-white w-full max-w-lg max-h-[90dvh] sm:max-h-[92vh] overflow-auto rounded-t-2xl sm:rounded-lg" onClick={e=>e.stopPropagation()}>
        <div className="sticky top-0 bg-white p-4 sm:p-5 border-b border-slate-200 flex items-start justify-between z-10 rounded-t-2xl sm:rounded-t-lg">
          <div>
            <h3 className="font-display text-xl font-bold">Assign Trip</h3>
            <div className="text-xs text-slate-500 mt-0.5">{day} · {mLbl(form.start_min)}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
        </div>
        <div className="p-5 space-y-3">
          <Lbl label="Route *">
            <select value={form.route_id} onChange={e=>setForm({...form, route_id:e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
              <option value="">— Select Route —</option>
              {routes.map((r, idx) => <option key={`${r.route_id}__${idx}`} value={r.route_id}>{`${r.route_id} · ${r.name}`}</option>)}
            </select>
          </Lbl>
          <div className="grid grid-cols-2 gap-2">
            <Lbl label="Start"><input type="time" value={mLbl(form.start_min)} onChange={e=>setForm({...form, start_min:tmm(e.target.value)})} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/></Lbl>
            <Lbl label="Duration (min)"><input type="number" value={form.duration_min} onChange={e=>setForm({...form, duration_min:+e.target.value})} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/></Lbl>
          </div>
          <Sel icon={Bus} label="Bus" value={form.bus_id} on={v=>setForm({...form, bus_id:v})} options={buses.map(b=>({value:b.fleet_id, label:`${b.fleet_id} · ${fmtDepot(b.depot, depotMap)}`}))} placeholder="(optional)"/>
          <Sel icon={UserCircle} label="Driver" value={form.driver_id} on={v=>setForm({...form, driver_id:v})} options={drivers.map(d=>({value:d.name, label:`${d.name} · ${fmtDepot(d.depot, depotMap)}`}))} placeholder="(optional)"/>
          <Sel icon={IdentificationCard} label="Conductor" value={form.conductor_id} on={v=>setForm({...form, conductor_id:v})} options={conductors.map(c=>({value:c.name, label:`${c.name} · ${fmtDepot(c.depot, depotMap)}`}))} placeholder="(optional)"/>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-md text-sm">Cancel</button>
          <button data-testid="quick-assign-submit" disabled={busy} onClick={submit} className="ktcl-btn-primary px-5 py-2 rounded-md text-sm font-semibold disabled:opacity-60">{busy ? "Assigning…" : "Assign Trip"}</button>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="label-cap text-slate-500 mb-2">{title}</div>
    {children}
  </div>
);
const Lbl = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide mb-1">{label}</div>
    {children}
  </div>
);
const Sel = ({ icon: Icon, label, value, on, options, placeholder }) => (
  <Lbl label={label}>
    <div className="relative">
      {Icon && <Icon size={14} className="absolute top-1/2 -translate-y-1/2 left-2.5 text-slate-400 pointer-events-none"/>}
      <select value={value} onChange={e=>on(e.target.value)} className={`w-full ${Icon?"pl-8":"pl-3"} pr-2 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none`}>
        <option value="">{placeholder || "Select"}</option>
        {options.map((o, idx) => <option key={`${o.value}__${idx}`} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  </Lbl>
);
